import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './features/auth/useAuth';
import { PlanProvider } from './contexts/PlanContext';
import { TooltipProvider } from './components/ui/Tooltip';
import { PageLoadingSpinner } from './components/ui/LoadingSpinner';
import { ErrorBoundary } from './components/ErrorBoundary';
import { queryClient } from './lib/react-query';
// import { WebSocketProvider } from './contexts/WebSocketContext'; // Removed - using globalWebSocketManager instead
import { HomePage } from './pages/HomePage';
import PrivateRoute from './components/PrivateRoute';
import { DashboardLayout, PublicLayout, AuthLayout } from './layouts';
import { LanguageManager } from './components/LanguageManager';
import { usePageTracking } from './hooks/useAnalytics';
import { CookieConsent } from './components/CookieConsent';
import { ThemeSyncProvider } from './components/ThemeSyncProvider';

// Lazy load pages
const PlanSelectionPage = React.lazy(() => import('./pages/auth/PlanSelectionPage'));
const RegisterPage = React.lazy(() => import('./pages/auth/RegisterPage'));
const LoginPage = React.lazy(() => import('./pages/auth/LoginPage'));
const VerifyEmailPage = React.lazy(() => import('./pages/auth/VerifyEmailPage'));
const OAuthCallbackPage = React.lazy(() => import('./pages/auth/OAuthCallbackPage'));
const OAuthErrorPage = React.lazy(() => import('./pages/auth/OAuthErrorPage'));
const ServicesPage = React.lazy(() => import('./pages/dashboard/ServicesPage'));
const BillingPage = React.lazy(() => import('./pages/dashboard/BillingPage'));
const SettingsPage = React.lazy(() => import('./pages/dashboard/SettingsPage'));
const PlanChangePage = React.lazy(() => import('./pages/dashboard/PlanChangePage'));

// Payment pages
const NewOrderPaymentPage = React.lazy(() => import('./pages/dashboard/payments/new/NewOrderPaymentPage'));
const NewOrderPaymentCompletePage = React.lazy(() => import('./pages/dashboard/payments/new/NewOrderPaymentCompletePage'));
const RetryPaymentPage = React.lazy(() => import('./pages/dashboard/payments/retry/RetryPaymentPage'));
const RetryPaymentCompletePage = React.lazy(() => import('./pages/dashboard/payments/retry/RetryPaymentCompletePage'));
const PlanChangePaymentPage = React.lazy(() => import('./pages/dashboard/payments/plan-change/PlanChangePaymentPage'));
const PlanChangePaymentCompletePage = React.lazy(() => import('./pages/dashboard/payments/plan-change/PlanChangePaymentCompletePage'));
const PlanExtendPaymentPage = React.lazy(() => import('./pages/dashboard/payments/extend/PlanExtendPaymentPage'));
const PlanExtendPaymentCompletePage = React.lazy(() => import('./pages/dashboard/payments/extend/PlanExtendPaymentCompletePage'));

// Service management pages
const ServiceDetailPage = React.lazy(() => import('./pages/dashboard/ServiceDetailPage'));
const ServiceOverviewPage = React.lazy(() => import('./pages/dashboard/ServiceOverviewPage'));
const VPNManagementPage = React.lazy(() => import('./pages/services/VPNManagementPageSimple'));
const SambaManagementPage = React.lazy(() => import('./pages/services/SambaManagementPageSimple'));
const NFSManagementPage = React.lazy(() => import('./pages/services/NFSManagementPageSimple'));
const FTPManagementPage = React.lazy(() => import('./pages/services/FTPManagementPage'));
const ServiceMonitoringPage = React.lazy(() => import('./pages/dashboard/ServiceMonitoringPage'));
const FTPAdvancedSettings = React.lazy(() => import('./pages/services/FTPAdvancedSettings'));
const UserGroupsManagementPage = React.lazy(() => import('./pages/services/UserGroupsManagementPageSimple'));
const ServiceSettingsPage = React.lazy(() => import('./pages/services/ServiceSettingsPage'));
const BackupsPage = React.lazy(() => import('./pages/dashboard/BackupsPage'));

// Activity logs page
const ActivityLogsPage = React.lazy(() => import('./pages/dashboard/ActivityLogsPage'));

// Password reset pages
const ForgotPasswordPage = React.lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/auth/ResetPasswordPage'));

// 404 page
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

// Static pages
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const FAQPage = React.lazy(() => import('./pages/FAQPage'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));
const DocsPage = React.lazy(() => import('./pages/DocsPage'));

// Solution pages
const SecureAccessPage = React.lazy(() => import('./pages/solutions/SecureAccessPage'));
const TeamDrivePage = React.lazy(() => import('./pages/solutions/TeamDrivePage'));
const BusinessStoragePage = React.lazy(() => import('./pages/solutions/BusinessStoragePage'));

// Using the shared LoadingSpinner component from components/ui/LoadingSpinner

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/pricing" element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <PlanSelectionPage />
          </Suspense>
        } />
        
        {/* Static pages */}
        <Route path="/about" element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <AboutPage />
          </Suspense>
        } />
        <Route path="/contact" element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ContactPage />
          </Suspense>
        } />
        <Route path="/faq" element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <FAQPage />
          </Suspense>
        } />
        <Route path="/privacy" element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <PrivacyPage />
          </Suspense>
        } />
        <Route path="/terms" element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <TermsPage />
          </Suspense>
        } />
        <Route path="/docs" element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <DocsPage />
          </Suspense>
        } />
        
        {/* Solution pages */}
        <Route path="/solutions/secure-access" element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <SecureAccessPage />
          </Suspense>
        } />
        <Route path="/solutions/team-drive" element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <TeamDrivePage />
          </Suspense>
        } />
        <Route path="/solutions/business-storage" element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <BusinessStoragePage />
          </Suspense>
        } />
      </Route>

      {/* Auth routes */}
      <Route element={<AuthLayout />}>
        <Route path="/auth/register" element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <RegisterPage />
          </Suspense>
        } />
        <Route path="/auth/login" element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <LoginPage />
          </Suspense>
        } />
        <Route path="/auth/verify-email" element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <VerifyEmailPage />
          </Suspense>
        } />
        <Route path="/auth/oauth/callback" element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <OAuthCallbackPage />
          </Suspense>
        } />
        <Route path="/auth/oauth/error" element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <OAuthErrorPage />
          </Suspense>
        } />
        <Route path="/auth/plans" element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <PlanSelectionPage />
          </Suspense>
        } />
        <Route path="/auth/forgot-password" element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ForgotPasswordPage />
          </Suspense>
        } />
        <Route path="/auth/reset-password" element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ResetPasswordPage />
          </Suspense>
        } />
      </Route>

      {/* Payment completion routes - not protected */}
      <Route path="/dashboard/payments">
        <Route path="new">
          <Route path="complete" element={
            <Suspense fallback={<PageLoadingSpinner />}>
              <NewOrderPaymentCompletePage />
            </Suspense>
          } />
          <Route path=":orderId/payment/complete" element={
            <Suspense fallback={<PageLoadingSpinner />}>
              <NewOrderPaymentCompletePage />
            </Suspense>
          } />
        </Route>
        <Route path="retry/:orderId/payment/complete" element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <RetryPaymentCompletePage />
          </Suspense>
        } />
        <Route path="plan-change/:planChangeId/payment/complete" element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <PlanChangePaymentCompletePage />
          </Suspense>
        } />
        <Route path="extend/:serviceId/payment/complete" element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <PlanExtendPaymentCompletePage />
          </Suspense>
        } />
      </Route>

      {/* Protected dashboard routes */}
      <Route element={<PrivateRoute><DashboardLayout /></PrivateRoute>}>
        <Route path="/dashboard">
          <Route index element={<Navigate to="/dashboard/services" replace />} />
          <Route path="services" element={
            <Suspense fallback={<PageLoadingSpinner />}>
              <ServicesPage />
            </Suspense>
          } />
          <Route path="plans">
            <Route index element={
              <Suspense fallback={<PageLoadingSpinner />}>
                <PlanSelectionPage />
              </Suspense>
            } />
            <Route path="change" element={
              <Suspense fallback={<PageLoadingSpinner />}>
                <PlanChangePage />
              </Suspense>
            } />
          </Route>
          <Route path="payments">
            <Route path="new">
              <Route path="payment" element={
                <Suspense fallback={<PageLoadingSpinner />}>
                  <NewOrderPaymentPage />
                </Suspense>
              } />
            </Route>
            <Route path="retry/:orderId">
              <Route path="payment" element={
                <Suspense fallback={<PageLoadingSpinner />}>
                  <RetryPaymentPage />
                </Suspense>
              } />
            </Route>
            <Route path="plan-change/:planChangeId">
              <Route path="payment" element={
                <Suspense fallback={<PageLoadingSpinner />}>
                  <PlanChangePaymentPage />
                </Suspense>
              } />
            </Route>
            <Route path="extend/:serviceId">
              <Route path="payment" element={
                <Suspense fallback={<PageLoadingSpinner />}>
                  <PlanExtendPaymentPage />
                </Suspense>
              } />
            </Route>
          </Route>
          <Route path="billing" element={
            <Suspense fallback={<PageLoadingSpinner />}>
              <BillingPage />
            </Suspense>
          } />
          <Route path="settings" element={
            <Suspense fallback={<PageLoadingSpinner />}>
              <SettingsPage />
            </Suspense>
          } />
          <Route path="activity" element={
            <Suspense fallback={<PageLoadingSpinner />}>
              <ActivityLogsPage />
            </Suspense>
          } />
          
          {/* Service detail routes */}
          <Route path="services/:serviceId" element={
            <Suspense fallback={<PageLoadingSpinner />}>
              <ServiceDetailPage />
            </Suspense>
          }>
            <Route index element={
              <Suspense fallback={<PageLoadingSpinner />}>
                <ServiceOverviewPage />
              </Suspense>
            } />
            <Route path="vpn" element={
              <Suspense fallback={<PageLoadingSpinner />}>
                <VPNManagementPage />
              </Suspense>
            } />
            <Route path="samba" element={
              <Suspense fallback={<PageLoadingSpinner />}>
                <SambaManagementPage />
              </Suspense>
            } />
            <Route path="nfs" element={
              <Suspense fallback={<PageLoadingSpinner />}>
                <NFSManagementPage />
              </Suspense>
            } />
            <Route path="ftp" element={
              <Suspense fallback={<PageLoadingSpinner />}>
                <FTPManagementPage />
              </Suspense>
            } />
            <Route path="ftp-advanced" element={
              <Suspense fallback={<PageLoadingSpinner />}>
                <FTPAdvancedSettings />
              </Suspense>
            } />
            <Route path="backups" element={
              <Suspense fallback={<PageLoadingSpinner />}>
                <BackupsPage />
              </Suspense>
            } />
            <Route path="users" element={
              <Suspense fallback={<PageLoadingSpinner />}>
                <UserGroupsManagementPage />
              </Suspense>
            } />
            <Route path="monitoring" element={
              <Suspense fallback={<PageLoadingSpinner />}>
                <ServiceMonitoringPage />
              </Suspense>
            } />
            <Route path="settings" element={
              <Suspense fallback={<PageLoadingSpinner />}>
                <ServiceSettingsPage />
              </Suspense>
            } />
          </Route>
        </Route>
      </Route>

      {/* Catch all route - 404 page */}
      <Route path="*" element={
        <Suspense fallback={<PageLoadingSpinner />}>
          <NotFoundPage />
        </Suspense>
      } />
    </Routes>
  );
}

function AppContent() {
  const { user, isLoading } = useAuth();
  
  // Enable automatic page tracking
  usePageTracking();

  // Show loading state while auth is initializing
  if (isLoading) {
    return <PageLoadingSpinner />;
  }

  return (
    <ErrorBoundary>
      <ThemeProvider initialTheme={user?.theme as 'light' | 'dark' | 'system'}>
        <ThemeSyncProvider>
          <TooltipProvider>
            <LanguageManager />
            <AppRoutes />
            <CookieConsent />
          </TooltipProvider>
        </ThemeSyncProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <PlanProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </PlanProvider>
      </BrowserRouter>
      {/* Only show React Query DevTools in development */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default App;
